import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import { useForceDark } from "@/hooks/useForceDark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = "https://images.unsplash.com/photo-1704633785114-52104ce3d626?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHBpdGNoJTIwYWVyaWFsJTIwZ3Jhc3N8ZW58MHx8fHwxNzg3MDYxNjM2fDA&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  useForceDark();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 6;
  const mismatch = pw2.length > 0 && pw !== pw2;
  const canSubmit = !!token && pw.length >= 6 && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password updated — sign in with your new password");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0F1C] via-[#0A0F1C]/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
            <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
          </Link>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Almost there</div>
            <h1 className="font-display font-black text-5xl mt-3 leading-tight">Choose<br/>a new<br/>password.</h1>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-start px-6 md:px-16 py-16">
        {!token ? (
          <div className="w-full max-w-md space-y-4" data-testid="reset-no-token">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Invalid link</div>
            <h2 className="font-display font-black text-4xl">Something went wrong</h2>
            <p className="text-white/60 text-sm">This page needs a reset token. Request a fresh reset link and try again.</p>
            <Link to="/forgot-password" className="btn-volt inline-block px-6 py-3 rounded-md">Request new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="reset-form">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Reset password</div>
              <h2 className="font-display font-black text-4xl mt-2">Set a new password</h2>
              <p className="mt-3 text-white/60 text-sm">Pick something you'll remember — at least 6 characters.</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="pw" className="text-xs tracking-widest uppercase text-white/60">New password</Label>
                <Input id="pw" type="password" required value={pw} onChange={e=>setPw(e.target.value)} data-testid="reset-pw-input" className="mt-2 bg-[#111827] border-white/10 focus:border-[#83B68B] focus:ring-[#83B68B]/30" />
                {tooShort && <p className="text-[11px] text-red-300 mt-1" data-testid="reset-pw-tooshort">At least 6 characters.</p>}
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs tracking-widest uppercase text-white/60">Confirm password</Label>
                <Input id="pw2" type="password" required value={pw2} onChange={e=>setPw2(e.target.value)} data-testid="reset-pw2-input" className="mt-2 bg-[#111827] border-white/10 focus:border-[#83B68B] focus:ring-[#83B68B]/30" />
                {mismatch && <p className="text-[11px] text-red-300 mt-1" data-testid="reset-pw-mismatch">Passwords don't match.</p>}
              </div>
            </div>
            <button type="submit" disabled={busy || !canSubmit} data-testid="reset-submit-btn" className="btn-volt w-full py-3 rounded-md disabled:opacity-40">
              {busy ? "Updating…" : "Update password"}
            </button>
            <div className="text-sm text-white/60 text-center">
              <Link to="/login" className="text-[#83B68B] hover:underline" data-testid="reset-to-login-link">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
